import React from 'react';
import { useData, useField, toReportDateTime, noteTimeRange } from '../EpcrDataContext';
import { Icons } from '../EpcrIcons';
import { TimesSummary } from './EpcrTimes';
import { TextField, OptionField, AutoField, StaticField, PickerModal, Section, ViewLayout, Toast } from '../EpcrFields';

const URLS = require('../../../../urls.js');

const CREW_ROSTER_URL = '/epcr-data/crew_roster.json';
const DESTINATION_HOSPITALS_URL = '/epcr-data/destination_hospitals.json';

// "Update CAD" — canned scene data, always the same dispatch address for this
// demo feed. Gainesville, GA (Hall County): the same city already used by
// the sample seizure report and the City.json picker list, so the codes
// resolve to real labels rather than bare digits.
const CAD_SCENE = {
  "eScene.15": "482 Riverbend Trail",  // Incident Street Address
  "eScene.17": "2403675",              // Incident City — Gainesville, GA (GNIS)
  "eScene.18": "13",                   // Incident State — Georgia (ANSI)
  "eScene.21": "13139",                // Incident County — Hall County (FIPS)
  "eScene.19": "30501",                // Incident ZIP Code
  "eScene.09": "Y92.0",                // Incident Location Type — Private residence
};

// Used only if the LLM call itself fails (network error, bad JSON, etc.) —
// same numbers the backend already falls back to per-field when its response
// is missing/malformed (see generateEpcrTimeEstimate in llm-controller.js),
// so both paths agree on what "reasonable default timing" looks like.
const FALLBACK_TIME_OFFSETS = {
  beforeEarliestMin: { arrivedAtPatient: 1, arrivedOnScene: 3, unitEnRoute: 11, unitNotified: 12, dispatchNotified: 13, psapCall: 15 },
  afterLatestMin: { unitLeftScene: 5, arrivedDestination: 17, transferOfCare: 27, backInService: 32 },
};

// Turns {beforeEarliestMin, afterLatestMin} minute-offsets (LLM-assisted, or
// the fixed fallback above) into actual eTimes.* Date objects, anchored to
// the attached voice notes' own earliest/latest timestamp — the offsets
// themselves are the only thing that ever varies between the two sources.
function timesFromOffsets(range, offsets) {
  const { earliest, latest } = range;
  const addMin = (d, m) => new Date(d.getTime() + m * 60000);
  const b = offsets.beforeEarliestMin, a = offsets.afterLatestMin;
  return {
    "eTimes.07": addMin(earliest, -b.arrivedAtPatient),
    "eTimes.06": addMin(earliest, -b.arrivedOnScene),
    "eTimes.05": addMin(earliest, -b.unitEnRoute),
    "eTimes.03": addMin(earliest, -b.unitNotified),
    "eTimes.02": addMin(earliest, -b.dispatchNotified),
    "eTimes.01": addMin(earliest, -b.psapCall),
    "eTimes.09": addMin(latest, a.unitLeftScene),
    "eTimes.11": addMin(latest, a.arrivedDestination),
    // An older backend may not return transferOfCare yet — fall back to
    // halfway between arriving at the destination and back in service.
    "eTimes.12": addMin(latest, a.transferOfCare ?? Math.round((a.arrivedDestination + a.backInService) / 2)),
    "eTimes.13": addMin(latest, a.backInService),
  };
}

// Asks the backend for content-aware phase-timing offsets (see
// epcr_times_system.txt) — the LLM only ever returns small integers, never a
// formatted date/time itself (unreliable at exact date arithmetic); this
// module does the actual date math. Returns null on any failure so the
// caller can fall back to the fixed offsets above.
async function fetchLlmTimeOffsets(attachedNotes) {
  try {
    const resp = await fetch(URLS.GENERATE_EPCR_DEMO_DATA, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'times',
        notes: attachedNotes.map(n => ({ id: n.uid, timestamp: n.timestamp, text: n.text })),
      }),
    });
    const result = await resp.json();
    return result?.times || null;
  } catch (e) {
    console.error('[Update CAD] LLM time estimate failed, using fallback offsets:', e);
    return null;
  }
}

async function applyCadUpdate(data, attachedNotes) {
  const range = noteTimeRange(attachedNotes);
  if (!range) return false;

  for (const [elem, value] of Object.entries(CAD_SCENE)) {
    const path = data.path(elem);
    if (path) data.setReportPath(path, value);
  }

  const offsets = (await fetchLlmTimeOffsets(attachedNotes)) || FALLBACK_TIME_OFFSETS;
  const times = timesFromOffsets(range, offsets);
  for (const [elem, date] of Object.entries(times)) {
    const path = data.path(elem);
    if (path) data.setReportPath(path, toReportDateTime(date));
  }
  return true;
}

// eCrew.03 (Crew Member Response Role, maxOccurs "M") collapsed to a single
// three-way choice for the UI. Each choice writes both the "At Scene" and
// "Transport" variant of that role, so one designation holds for the whole
// call rather than asking the user to reason about NEMSIS's scene/transport
// phase split.
const ROLE_CODES = {
  primary: ["2403011", "2403013"], // Primary Patient Caregiver – At Scene / Transport
  driver: ["2403001", "2403003"],  // Driver/Pilot – Response / Transport
  other: ["2403005"],              // Other
};
const ROLE_ORDER = [["primary", "Primary"], ["driver", "Driver"], ["other", "Other"]];

function roleKeyFromCodes(raw) {
  const codes = Array.isArray(raw) ? raw : raw ? [raw] : [];
  for (const key of Object.keys(ROLE_CODES)) {
    if (ROLE_CODES[key].some(c => codes.includes(c))) return key;
  }
  return null;
}

export function EpcrViewIncident({ attachedNotes = [] }) {
  const data = useData();
  const crew = useField("eCrew/eCrew.CrewGroup");
  const crewList = Array.isArray(crew) ? crew : [];

  const [cadError, setCadError] = React.useState(false);
  const [cadBusy, setCadBusy] = React.useState(false);
  const [toast, setToast] = React.useState(null);

  const handleUpdateCad = async () => {
    if (cadBusy) return;
    setCadBusy(true);
    const ok = await applyCadUpdate(data, attachedNotes);
    setCadBusy(false);
    if (!ok) {
      setCadError(true);
      setTimeout(() => setCadError(false), 2000);
      return;
    }
    setCadError(false);
    setToast('CAD data updated.');
  };

  // Selecting an Incident City auto-fills Incident County from the same
  // GNIS record (City.json carries each city's county FIPS code). County is
  // now also a picker (County.json ships all 159 real Georgia counties, so
  // any GA county — auto-filled or manually picked — resolves to plaintext).
  const handleCityPick = (cityCode) => {
    const entry = data.options("eScene.17").find(o => o.value === cityCode);
    if (entry?.countyFips) data.setReportPath("eScene/eScene.21", entry.countyFips);
  };

  const sections = [
    { id: "response",    label: "Response" },
    { id: "scene",       label: "Scene" },
    { id: "personnel",   label: "Personnel" },
    { id: "disposition", label: "Disposition" },
    { id: "destination", label: "Destination" },
    { id: "times",       label: "Times" },
    { id: "mileage",     label: "Mileage" },
    { id: "additional",  label: "Additional Factors" },
  ];

  return (
    <>
    <ViewLayout sections={sections}>

      <Section id="response" num="01" title="Response" meta="eResponse.* · eDispatch.*">
        <div className="fgrid">
          <div className="fcol-10">
            <TextField label="Incident Number" elem="eResponse.03" />
          </div>
          <div className="fcol-2" style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              className={"btn-secondary" + (cadError ? " btn-error" : "")}
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleUpdateCad}
              disabled={cadBusy}
              title={cadError ? "No timestamped voice notes attached to this call" : undefined}
            >
              <Icons.swap size={14} /> {cadBusy ? "Updating…" : "Update CAD"}
            </button>
          </div>
          <div className="fcol-12">
            <TextField label="EMS Response Number" elem="eResponse.04" />
          </div>
          <div className="fcol-12">
            <OptionField label="Type of Service Requested" elem="eResponse.05"
              featured={["2205001", "2205009"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Response Mode to Scene" elem="eResponse.23"
              featured={["2223001", "2223003", "2223005"]} />
          </div>
          <div className="fcol-12">
            {/* maxOccurs "M" — AutoField dispatches to MultiOptionField;
                shortcuts give quick toggle buttons for the two most common
                picks, same mechanism as the Delay fields below. */}
            <AutoField label="Additional Response Mode Descriptors" elem="eResponse.24"
              shortcuts={[
                { label: "Lights and Sirens", value: "2224015" },
                { label: "No Lights or Sirens", value: "2224019" },
              ]} />
          </div>
          <div className="fcol-12">
            <TextField label="Vehicle Dispatch Location" elem="eResponse.16" />
          </div>
          <div className="fcol-12">
            <TextField label="EMS Vehicle (Unit) Number" elem="eResponse.13" />
          </div>
          <div className="fcol-12">
            <OptionField label="Unit Transport and Equipment Capability" elem="eResponse.07" />
          </div>
          <div className="fcol-12">
            <TextField label="EMS Unit Call Sign" elem="eResponse.14" />
          </div>
          <div className="fcol-12">
            <OptionField label="Dispatch Reason" elem="eDispatch.01"
              featured={["2301013", "2301061", "2301069"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Dispatch Priority (Patient Acuity)" elem="eDispatch.05" />
          </div>
        </div>
      </Section>

      <Section id="scene" num="02" title="Scene" meta="eScene.*">
        <div className="fgrid">
          <div className="fcol-12">
            <OptionField label="Incident Location Type" elem="eScene.09" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident Facility or Location Name" elem="eScene.13" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident Street Address" elem="eScene.15" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident ZIP Code" elem="eScene.19" />
          </div>
          <div className="fcol-12">
            <OptionField label="Incident City" elem="eScene.17" onPick={handleCityPick} />
          </div>
          <div className="fcol-12">
            <OptionField label="Incident County" elem="eScene.21" />
          </div>
          <div className="fcol-12">
            <OptionField label="Incident State" elem="eScene.18" />
          </div>
          <div className="fcol-12">
            <TextField label="Incident Country" elem="eScene.22" />
          </div>
        </div>
      </Section>

      <Section id="personnel" num="03" title="Personnel"
        meta={`eCrew.* · ${crewList.length} member${crewList.length === 1 ? "" : "s"}`}>
        <CrewList data={data} crewList={crewList} />
      </Section>

      <Section id="disposition" num="04" title="Disposition" meta="eDisposition.*">
        <div className="fgrid">
          <div className="fcol-12">
            <OptionField label="Unit Disposition" elem="eDisposition.27" featured={["4227001"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Patient Evaluation/Care" elem="eDisposition.28" featured={["4228001"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Crew Disposition" elem="eDisposition.29" featured={["4229001"]} />
          </div>
          <div className="fcol-12">
            <OptionField label="Transport Disposition" elem="eDisposition.30" featured={["4230001"]} />
          </div>
        </div>
      </Section>

      <Section id="destination" num="05" title="Destination" meta="eDisposition.Destination*">
        <div className="fgrid">
          <div className="fcol-12">
            <DestinationNameField />
          </div>
          <div className="fcol-12">
            <TextField label="Destination Address" elem="eDisposition.03" />
          </div>
          <div className="fcol-12">
            <TextField label="Destination ZIP" elem="eDisposition.07" />
          </div>
          <div className="fcol-12">
            <OptionField label="Type of Destination" elem="eDisposition.21" />
          </div>
          <div className="fcol-12">
            <AutoField label="Reason for Destination Choice" elem="eDisposition.20" />
          </div>
        </div>
      </Section>

      <Section id="times" num="06" title="Times" meta="eTimes.* · eResponse.08–12">
        <div className="fgrid">
          <div className="fcol-12">
            <TimesSummary />
          </div>

          {/* Delay fields */}
          <div className="fcol-12">
            <AutoField label="Dispatch Delays" elem="eResponse.08"
              shortcuts={[{ label: "None/No Delay", value: "2208013" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Response Delays" elem="eResponse.09"
              shortcuts={[{ label: "None/No Delay", value: "2209011" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Scene Delays" elem="eResponse.10"
              shortcuts={[{ label: "None/No Delay", value: "2210017" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Transport Delays" elem="eResponse.11"
              shortcuts={[{ label: "None/No Delay", value: "2211011" }]} />
          </div>
          <div className="fcol-12">
            <AutoField label="Turn-Around Delays" elem="eResponse.12"
              shortcuts={[{ label: "None/No Delay", value: "2212015" }]} />
          </div>
        </div>
      </Section>

      <Section id="mileage" num="07" title="Mileage" meta="eResponse.19–22">
        <div className="fgrid">
          <div className="fcol-6"><TextField label="Begin Odometer" elem="eResponse.19" nem={null} /></div>
          <div className="fcol-6"><TextField label="On Scene Odometer" elem="eResponse.20" nem={null} /></div>
          <div className="fcol-6"><TextField label="At Destination Odometer" elem="eResponse.21" nem={null} /></div>
          <div className="fcol-6"><TextField label="End Odometer" elem="eResponse.22" nem={null} /></div>
          <div className="fcol-12">
            <MileageSummary />
          </div>
        </div>
      </Section>

      <Section id="additional" num="08" title="Additional Factors" meta="eScene.* · custom">
        <div className="fgrid">
          <div className="fcol-12">
            <TextField label="Other EMS or Public Safety Agencies at Scene" elem="eScene.02" />
          </div>
          <div className="fcol-12">
            <OptionField label="First Agency to Provide Patient Care" elem="eScene.24"
              inline={false} />
          </div>
          <div className="fcol-12">
            <OptionField label="Additional Responders" elem="eScene.04" />
          </div>
          <div className="fcol-12">
            <TextField label="Additional Comments" path="custom/AdditionalComments" multiline rows={3} />
          </div>
        </div>
      </Section>

    </ViewLayout>
    {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </>
  );
}

function CrewList({ data, crewList }) {
  const [rosterOpen, setRosterOpen] = React.useState(false);
  const [roster, setRoster] = React.useState([]);

  // The roster is a single small static file (~10 rows) — fetched once on
  // mount rather than lazily on open, so the "Add crew member" picker never
  // has to show a loading state.
  React.useEffect(() => {
    let cancelled = false;
    fetch(CREW_ROSTER_URL).then(r => r.json()).then(list => { if (!cancelled) setRoster(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setCrew = (next) => data.setReportPath("eCrew/eCrew.CrewGroup", next);

  const addMember = (id) => {
    const entry = roster.find(r => r.id === id);
    if (!entry) return;
    setCrew([...crewList, { "eCrew.01": entry.id, "eCrew.02": entry.level }]);
    setRosterOpen(false);
  };

  const removeMember = (i) => setCrew(crewList.filter((_, idx) => idx !== i));

  const setRole = (i, key) => data.setReportPath(`eCrew/eCrew.CrewGroup/${i}/eCrew.03`, ROLE_CODES[key]);

  const existingIds = new Set(crewList.map(c => c["eCrew.01"]).filter(Boolean));
  const rosterOptions = roster
    .filter(r => !existingIds.has(r.id))
    .map(r => ({
      value: r.id,
      label: `${r.name} — ${data.label("eCrew.02", r.level) || r.level}`,
      category: r.id.startsWith("P") ? "Paramedic (P-series)" : "EMT-tier (E-series)",
    }));

  return (
    <div className="epcr-crew-list">
      {crewList.map((c, i) => {
        const id = c["eCrew.01"] || "";
        const initials = id.slice(0, 2).toUpperCase();
        const roleKey = roleKeyFromCodes(c["eCrew.03"]);
        return (
          <div key={i} className="epcr-crew-row">
            <div className="epcr-crew-avatar">{initials || "—"}</div>
            <StaticField label="Crew Member ID" value={id} mono />
            <div className="field epcr-crew-role">
              <label className="field-label"><span>Role</span></label>
              <div className="opt-group">
                {ROLE_ORDER.map(([key, label]) => (
                  <button
                    key={key}
                    className={"opt-btn" + (roleKey === key ? " selected blue" : "")}
                    onClick={() => setRole(i, key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button className="icon-btn" onClick={() => removeMember(i)} aria-label="Remove crew member">
              <Icons.trash size={16} />
            </button>
          </div>
        );
      })}
      {crewList.length === 0 && (
        <div className="epcr-crew-empty">No crew members recorded</div>
      )}
      <button className="btn-secondary epcr-crew-add" onClick={() => setRosterOpen(true)}>
        <Icons.plus size={14} /> Add crew member
      </button>
      {rosterOpen && (
        <PickerModal
          title="Add Crew Member"
          options={rosterOptions}
          onClose={() => setRosterOpen(false)}
          onSelect={addMember}
        />
      )}
    </div>
  );
}

// eDisposition.01 (Destination Name) has no NEMSIS enumeration — it's free
// text in the real spec. This picker is a demo-only convenience over a
// small fictional roster of 3 GA hospitals (tied to cities already in
// City.json — Gainesville, Atlanta, Savannah); picking one auto-fills the
// matching Destination Address/ZIP, same onPick side-effect mechanism as
// the Incident City -> County auto-fill above.
function DestinationNameField() {
  const data = useData();
  const [hospitals, setHospitals] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    fetch(DESTINATION_HOSPITALS_URL).then(r => r.json()).then(list => { if (!cancelled) setHospitals(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const options = hospitals.map(h => ({ value: h.name, label: h.name, category: "" }));

  const handlePick = (name) => {
    const hosp = hospitals.find(h => h.name === name);
    if (!hosp) return;
    // Resolved via data.path (not hardcoded) — eDisposition.03/.07 live under
    // eDisposition.DestinationGroup, a nesting the elem-driven fields already
    // account for automatically.
    const addrPath = data.path("eDisposition.03");
    const zipPath = data.path("eDisposition.07");
    if (addrPath) data.setReportPath(addrPath, hosp.address);
    if (zipPath) data.setReportPath(zipPath, hosp.zip);
  };

  // inline={false}: with only 3 options, OptionField's auto-inline heuristic
  // (<=4 options) would otherwise render three long-text pill buttons instead
  // of the same "Select…" picker button City/State/County use — force the
  // picker style for visual consistency with those sibling fields.
  return <OptionField label="Destination Name" elem="eDisposition.01" options={options} onPick={handlePick} inline={false} />;
}

function MileageSummary() {
  const begin   = parseFloat(useField("eResponse/eResponse.19"));
  const onScene = parseFloat(useField("eResponse/eResponse.20"));
  const atDest  = parseFloat(useField("eResponse/eResponse.21"));
  const end     = parseFloat(useField("eResponse/eResponse.22"));
  const total    = isFinite(end) && isFinite(begin) ? (end - begin).toFixed(1) : null;
  const loaded   = isFinite(atDest) && isFinite(onScene) ? (atDest - onScene).toFixed(1) : null;
  const deadhead = total != null && loaded != null ? (parseFloat(total) - parseFloat(loaded)).toFixed(1) : null;
  const stats = [["Loaded Miles", loaded], ["Total Miles", total], ["Deadhead Miles", deadhead]];
  return (
    <div className="mileage-summary">
      {stats.map(([label, v]) => (
        <div className="mileage-stat" key={label}>
          <span className="label">{label}</span>
          <span className="value">{v ?? "—"}{v != null && <span className="unit">mi</span>}</span>
        </div>
      ))}
    </div>
  );
}
